/**
 * FORM DERIVE TYPES
 * Persisted shapes for Form Derived Values: the FormType-level derive
 * configuration, the per-version provenance state written only by the server
 * derive reconciler, and the ownership marker on projected ArTable rows.
 * Pure evaluation lives in `formDeriveEngine/`; consumers are the FormType
 * settings UI, the derive triggers/repair in functions/, and FormUI display.
 *
 * Invariant: every import here is `import type`, so the module erases at
 * compile time. `System.types` and `System.Artifacts.types` both import back
 * from this file and the cycle must never become a runtime one.
 */

import type { Timestamp } from 'firebase/firestore';
import type { Description, FieldId } from './System.types';
import type { ArColumnAggregation, ArRowId, ArTableId, TypeId } from './System.Artifacts.types';

/** Settings-UI-assigned, unique within one FormType, immutable once created. */
export type FormDeriveId = string;

/**
 * Property of the referenced EntityRecord, read LIVE from the record document —
 * never a snapshot taken when the reference was made, so a rename or a field
 * edit on the record shows up on the next derive pass.
 */
export type FormDeriveReferenceProp =
  /** The record's own `name`. */
  | { kind: 'name' }
  /** A value at `fields[fieldId].value` on the referenced record. */
  | { kind: 'field'; fieldId: FieldId }
  /** One hop further: a key in the referenced record's own `entityReferences`. */
  | { kind: 'reference'; referenceKey: string };

/** A value read from `FormVersion.fields[fieldId].value` on this very version. */
export type FormDeriveFieldSource = { kind: 'field'; fieldId: FieldId };

/** A single number folded out of one ArTable column across every data row. */
export type FormDeriveColAggregateSource = {
  /** Discriminant. */
  kind: 'colAggregate';
  /** Which ArTable subcollection on the FormVersion the rows are read from. */
  tableId: ArTableId;
  /** Which column of that table is folded. */
  typeId: TypeId;
  /** The fold applied to the column's cell values. */
  aggregation: ArColumnAggregation;
  /** Dot-path into composite value, same spelling as ArColumnField.aggregationFieldPath. */
  aggregationFieldPath?: string;
};

/**
 * One ArTable column, deduplicated to its distinct values — set-valued. Also
 * the partition key of a row projection; its distinct canonical values are the
 * projected row set.
 */
export type FormDeriveColUniquesSource = {
  /** Discriminant. */
  kind: 'colUniques';
  /** Which ArTable subcollection the source column belongs to. */
  tableId: ArTableId;
  /** Which column of that table is deduplicated. */
  typeId: TypeId;
};

/** A property read off an EntityRecord this FormVersion already references. */
export type FormDeriveReferencePropSource = {
  /** Discriminant. */
  kind: 'referenceProp';
  /** Key into FormVersion.entityReferences (entityGroupId || entityId). */
  referenceKey: string;
  /** Which property of the referenced record is read. */
  prop: FormDeriveReferenceProp;
};

/**
 * The sources that yield exactly one value. Deliberately EXCLUDES the
 * set-valued source `colUniques`: combining two set-valued sources is a join,
 * not arithmetic, and this subsystem does not do joins. A row projection's fold
 * over a partition of ONE table is not a join and stays legal, which is why no
 * row-value arm carries a `tableId`. Only these three may appear as operands of
 * an algebraic derive or as the source of a field target.
 */
export type FormDeriveScalarSource =
  | FormDeriveFieldSource
  | FormDeriveColAggregateSource
  | FormDeriveReferencePropSource;

/** One named input to an algebraic derive's expression. */
export type FormDeriveOperand = {
  /**
   * The explicit variable name the expression refers to. Explicit, not
   * positional, so reordering operands in the settings UI cannot silently
   * change what an expression means. Validated at save: `/^[a-z][a-zA-Z0-9]*$/`,
   * unique within the entry, and never a member of the evaluator's function map
   * (`min`, `max`, `sqrt`, `pow`, `if`, …) — an alias named `min` would shadow
   * the function and change the expression's meaning.
   */
  alias: string;
  /** Where the operand's single value is read from. */
  source: FormDeriveScalarSource;
};

/** A single value written to `FormVersion.fields[fieldId].value`. */
export type FormDeriveFieldTarget = {
  /** The field on this FormVersion that receives the derived value. */
  fieldId: FieldId;
};

/** The whole ArTable one row-projection derive owns. Must NOT carry a
 * `fieldId` — `'fieldId' in entry.target` is the system-wide narrowing — and
 * must not carry a `typeId`, so a draft can never half-match the retired col
 * target shape in `formDeriveTargetKey`. */
export type FormDeriveTableTarget = {
  /** Which ArTable subcollection receives the projected rows. */
  tableId: ArTableId;
};

/** The partition key itself: `effectiveCellValue` of the group's winner row
 * (lowest rowId), exactly what `colUniques` projected before this arm
 * subsumed it. Reads no column beyond the entry's `source.typeId`. */
export type FormDeriveGroupKeySource = { kind: 'groupKey'; roundToCents?: never };

/** One cell of the group's winner row — lowest rowId, the same winner
 * `groupKey` reads, never `order`. A blank cell projects as `null`, mirroring
 * the retired mirror arm. */
export type FormDeriveSourceCellSource = {
  kind: 'sourceCell';
  /**
   * Column of the entry's source table, or `parent.sub` addressing a composite
   * sub-field of one — the one SOURCE position where a dot is legal, validated
   * by `isColumnPathTypeId` and read by `readFormDeriveCellValue` from the same
   * `value.fields.<sub>.value` a dotted projection column writes.
   */
  typeId: TypeId;
  roundToCents?: never;
};

/** One number folded out of the GROUP's source rows — `computeColumnAggregation`
 * over the partition subset. Carries no tableId: every row-value arm reads the
 * entry's one source table, which is what keeps the subsystem's no-joins
 * refusal structural rather than validated. The ONE row-value arm that can
 * produce a fraction, hence the one that carries `roundToCents`. */
export type FormDeriveGroupAggregateSource = {
  kind: 'groupAggregate';
  /** Column of the entry's source table, undotted. */
  typeId: TypeId;
  /** The fold applied to the group's cell values. */
  aggregation: ArColumnAggregation;
  /** Dot-path into composite value, same spelling as ArColumnField.aggregationFieldPath. */
  aggregationFieldPath?: string;
  /** Round the folded number to cents. Meaningful here alone among row-value arms. */
  roundToCents?: boolean;
};

/** A property of the EntityRecord behind the group's reference cell, read from
 * the winner row's cell. The cell carries only a `docId`; the caller resolves
 * the record path from `ArColumnReferenceEntity.entityId` on the source
 * table's reference column and keys the engine's `records` map by that docId
 * (= entityRecordId). Read LIVE, like `FormDeriveReferenceProp` everywhere:
 * never the cell's `fields`/`entityReferences` snapshot. A group whose
 * reference cell holds no docId reads as `null`, not a throw. */
export type FormDeriveRowReferencePropSource = {
  kind: 'rowReferenceProp';
  /** The source table's reference column whose winner cell names the record. Undotted. */
  referenceTypeId: TypeId;
  /** Which property of the referenced record is read. */
  prop: FormDeriveReferenceProp;
  roundToCents?: never;
};

/** A literal a case yields or a condition compares against. Scalars only —
 * a reference or composite literal has no canonical authoring surface. */
export type FormDeriveCaseLiteral = string | number | boolean;

/** The right side of a condition: a literal, or another earlier-declared
 * column's alias. The object wrapper is what distinguishes the alias
 * `{ alias: 'threshold' }` from the string literal `'threshold'`. */
export type FormDeriveCaseOperand = FormDeriveCaseLiteral | { alias: string };

/**
 * One ANDed condition. `left` is the alias of a column THIS entry declares
 * EARLIER in its `columns` list — validated at save, never through the cycle
 * graph, and an undeclared column of the target table is refused outright.
 *
 * The operator restriction is split between the type and evaluation:
 * the TYPE pins the ordering operators to a numeric-or-alias right operand,
 * so `basis < 'abc'` is unrepresentable; the LEFT side's numeric requirement
 * is an evaluation rule — the aliased column's runtime type is unknowable at
 * save (`sourceCell` copies whatever the source holds), so at evaluation an
 * ordering comparison whose either side is not a finite number simply fails
 * the condition. Equality compares `canonicalizeFormDeriveValue` of both
 * sides, so `'abc' == 'abc'` and `false != true` work for strings and
 * booleans with no coercion.
 */
export type FormDeriveCaseCondition =
  | { left: string; op: '==' | '!='; right: FormDeriveCaseOperand }
  | { left: string; op: '<' | '<=' | '>' | '>='; right: number | { alias: string } };

/**
 * One guarded literal. `when` absent = the default arm, matching
 * unconditionally; first matching case wins; no match at all yields `null`.
 * An alias whose column evaluated to `null` fails EVERY condition, `!=`
 * included — a missing input never satisfies a guard, and the authored
 * default arm is the explicit place for "no answer".
 */
export type FormDeriveCase = {
  /** ANDed conditions; every one must hold. */
  when?: FormDeriveCaseCondition[];
  /** Written verbatim when this case wins; `null` blanks the cell. */
  value: FormDeriveCaseLiteral | null;
};

/** First matching guarded literal, over aliases bound to EARLIER columns of
 * this same entry. The formula evaluator is not involved: `evaluateFormula`'s
 * `if` is numeric-only, and the CLW rule branches on a string and a boolean. */
export type FormDeriveCasesSource = {
  kind: 'cases';
  cases: FormDeriveCase[];
  roundToCents?: never;
};

/**
 * Where one projected column's per-row value comes from. Evaluated against the
 * row's group only, left to right over the entry's `columns`. `roundToCents`
 * is pinned to `never` on the four arms that cannot produce a fraction, for
 * the same reason the entry union pins it: excess-property checking against a
 * union accepts an omitted member on every arm, so an unpinned flag would let
 * the settings UI persist a rounding the engine ignores.
 */
export type FormDeriveRowValueSource =
  | FormDeriveGroupKeySource
  | FormDeriveSourceCellSource
  | FormDeriveGroupAggregateSource
  | FormDeriveRowReferencePropSource
  | FormDeriveCasesSource;

/**
 * One declared column of a row projection. NO `description`: the column's
 * label is the ArTable column definition's, so `formDeriveSemanticEntries`
 * keeps stripping at entry level only and a label rename can never bump
 * `formDeriveRev`.
 */
export type FormDeriveProjectionColumn = {
  /**
   * The target column. One of the two positions in the subsystem where a dotted
   * typeId is legal — `sourceCell.typeId` is the other: `parent.sub` addresses
   * the composite sub-field exactly as `arFormulaTargetFieldSegments` does —
   * split at the FIRST dot, the sub-field id keeps any further dots — written
   * through those segments and read back through `readArStoredFormulaResult`'s
   * shape. Validated by `isColumnPathTypeId`, never `isColumnTypeId`. Unique
   * within the entry at save, and no column may equal another dotted column's
   * root (`parent` alongside `parent.sub` is two writers on one cell).
   */
  typeId: TypeId;
  /**
   * Binds this column's evaluated value for `cases` guards of LATER columns
   * in this same entry. Same grammar and reservations as
   * `FormDeriveOperand.alias`, unique within the entry, validated at save.
   * Optional: a column no guard reads needs none.
   */
  alias?: string;
  /** Where the column's per-row value comes from. */
  value: FormDeriveRowValueSource;
};

/**
 * Per-entry refresh configuration: the two triggers that take a parameter, both
 * off when absent. The other three — before a form state change, manual
 * refresh, and the `formDeriveRev` repair sweep — are always on and carry
 * nothing to configure, so they have no member here. Triggers STACK rather than
 * replace: the change trigger sees form-local writes alone, the interval
 * trigger sees everything by recomputing, and running both is the normal
 * configuration. Not semantic to any computed value, so
 * `formDeriveSemanticEntries` strips it alongside `description`.
 */
export type FormDeriveRefreshTriggers = {
  /**
   * Debounce window of the on-change trigger, in milliseconds; absent = off,
   * the default. At most one pass per window, and at least one per window while
   * anything material keeps changing, so steady typing cannot starve the pass.
   * Floored at `FORM_DERIVE_REFRESH_MIN_MS`. A suppressed change on a SOURCE
   * table is served by `formDeriveTrailingPass` at the window's end, which is
   * what makes the trailing edge a promise rather than a hope. A change on a
   * table an entry TARGETS is never debounced: that write is the tamper the
   * pass exists to repair, and no trailing pass is scheduled to catch it later.
   */
  onChangeDebounceMs?: number;
  /**
   * How often a pass runs while the form is open, in minutes; absent = off. The
   * only trigger that observes an edit made outside the form. Floored at
   * `FORM_DERIVE_REFRESH_MIN_MS`, converted to minutes by its reader.
   */
  whileOpenIntervalMinutes?: number;
};

/**
 * One configured derive: exactly the five legal (source, target) pairings, so an
 * aggregate into a table, an algebraic combination into a table, and a
 * uniques-set into a field are all UNREPRESENTABLE rather than rejected at save
 * time. Every arm carries `deriveId`, the stable identity the provenance stamps
 * and the projected rows' `deriveOwner` are keyed by, and `refresh`, the
 * per-entry cadence of the two configurable triggers.
 *
 * `roundToCents` is admissible on exactly the two arms that can produce a
 * non-integer number. The other three pin it to `never` rather than omitting it,
 * because TypeScript checks excess properties against the union as a whole: an
 * omitted member is silently accepted on every arm, which would let the settings
 * UI persist a rounding flag on a copy that the engine then ignores.
 *
 * Note for consumers: this is NOT narrowable from `entry.source.kind`. A nested
 * discriminant narrows `entry.source` but never `entry`, so `target` stays
 * widened. Narrow with `'source' in entry` first, then match on the source kind
 * through the engine's own predicates — never with a cast.
 *
 * `description` is authored by the FormType settings UI and carries the
 * codebase's own `Description`: `shortLabel` IS the derive's name, the string
 * every message and every settings row shows, and `longDescription` the admin's
 * note. The engine IGNORES it — no evaluation path reads it, so it can never
 * affect a computed value. That is precisely why `formDeriveRevBump` diffs it
 * away through `formDeriveSemanticEntries`: renaming a derive must not bump
 * `formDeriveRev` and must not enqueue a repair over every version of the type.
 */
export type FormDeriveEntry =
  /** Field → field: copy one FormVersion field value into another, faithfully. */
  | { deriveId: FormDeriveId; description?: Description; refresh?: FormDeriveRefreshTriggers; source: FormDeriveFieldSource;          target: FormDeriveFieldTarget; roundToCents?: never }
  /** Column aggregate → field. */
  | { deriveId: FormDeriveId; description?: Description; refresh?: FormDeriveRefreshTriggers; source: FormDeriveColAggregateSource;   target: FormDeriveFieldTarget; roundToCents?: boolean }
  /** Referenced-record property → field, copied faithfully. */
  | { deriveId: FormDeriveId; description?: Description; refresh?: FormDeriveRefreshTriggers; source: FormDeriveReferencePropSource;  target: FormDeriveFieldTarget; roundToCents?: never }
  /**
   * Row projection: one projected row per distinct canonical value of the
   * partition key, every declared column written on those rows. Row identity
   * is unchanged — `formDeriveProjectionRowId(deriveId, { kind: 'unique',
   * canonicalValue })` — and the `drv~<deriveId>~` namespace stays the sole
   * delete authority. The group key and every column arm read the entry's ONE
   * source table; grouping across two tables is a join and stays refused,
   * structurally: no column arm carries a tableId. Rounding is per column
   * (`groupAggregate.roundToCents`), so the entry-level flag stays `never`.
   */
  | { deriveId: FormDeriveId; description?: Description; refresh?: FormDeriveRefreshTriggers; source: FormDeriveColUniquesSource; target: FormDeriveTableTarget; columns: FormDeriveProjectionColumn[]; roundToCents?: never }
  /**
   * Algebraic combination of scalar operands → field, bound by alias.
   * `expression` is numeric-only: `+ - * /` and parentheses over the aliases,
   * evaluated by the table formula engine's `evaluateFormula`, unchanged.
   * Operand coercion is STRICT and diverges from table formulas: blank means
   * zero, garbage means no answer — a present, non-empty, non-numeric operand
   * yields `null` for the whole entry rather than a silent `0` in a locked
   * field. The engine never calls `toNumeric` on a derive operand.
   */
  | { deriveId: FormDeriveId; description?: Description; refresh?: FormDeriveRefreshTriggers; operands: FormDeriveOperand[]; expression: string; target: FormDeriveFieldTarget; roundToCents?: boolean };

/**
 * Why the engine wrote no value. `targetFormulaOwned` — an ArTable formula
 * already owns the target, so the derive yields to it. `cycle` — the derive
 * participates in a dependency cycle and is dropped from the ordering.
 */
export type FormDeriveSkipReason = 'targetFormulaOwned' | 'cycle';

/**
 * Per-derive provenance stamp, the analogue of `ArFormulaStamp` — with one
 * virtue deliberately given up. A row-formula stamp can be verified from the
 * row alone; this one cannot, because a derive's inputs live in other
 * documents, so checking `hash` means fetching them. What the stamp still buys
 * is the one-step fixpoint — a reconciler re-entered by its own write recomputes,
 * matches under an unchanged hash and exits without writing — plus `rev`, which
 * makes config-edit staleness a query against the FormType's `formDeriveRev`.
 * Absent stamp = never computed. Skipped derives carry `rev` and `skipped` but
 * no `inputs`/`hash`.
 */
export type FormDeriveStamp = {
  /** The FormType's `formDeriveRev` the value was computed under. */
  rev: number;
  /**
   * Sorted operand paths the fingerprint covers. Engine-derived, never
   * authored. Config-shaped: under `rowReferenceProp` the resolved record id
   * is a per-row fact, so `inputs` names operand SHAPES and only the per-row
   * hash material names documents — the weaker promise, deliberately.
   */
  inputs?: string[];
  /** Field/algebra entries: FNV-1a 64 fingerprint of the input values, 16 hex chars. */
  hash?: string;
  /**
   * Row projections: one fingerprint per projected row, keyed by the full
   * `drv~<deriveId>~<suffix>` rowId, covering that row's GROUP alone — so a
   * row rehashes only when its own group changes. The key set doubles as the
   * membership record: a group appearing or vanishing moves the map even when
   * every surviving hash matches. Exactly one of `hash`/`rowHashes` is
   * present on a computed stamp; a skipped stamp carries neither.
   */
  rowHashes?: Record<ArRowId, string>;
  /** Present when the engine skipped this derive instead of computing. */
  skipped?: FormDeriveSkipReason;
  /**
   * When the reconciler last WROTE this stamp. Never refreshed on a full
   * match: an unchanged `rowHashes` map suppresses the entire stamp write,
   * `computedAt` included, or the row trigger loops forever.
   */
  computedAt: Timestamp;
};

/**
 * Per-version derive state. Server-owned: every member is written by the
 * reconciler alone, with client writes denied by `canUpdateFormVersionFields`
 * in `firestore.rules`. The lock trio below follows the `computeAggregations`
 * protocol — a live lock makes a concurrent invocation set `recomputeRequested`
 * and exit rather than doing a second full pass.
 */
export type FormDeriveState = {
  /** The `formDeriveRev` the last full pass ran under. */
  rev?: number;
  /** Provenance for each derive that has run, keyed by `FormDeriveId`. */
  stamps?: Record<FormDeriveId, FormDeriveStamp>;
  /** Instance ID holding the computation lock, null when unlocked. */
  computingBy?: string | null;
  /** When the computation lock was acquired. */
  computingSince?: Timestamp | null;
  /**
   * Set by an invocation that found the lock held, so the running holder knows
   * an input changed after it read them and must recompute before stamping.
   */
  recomputeRequested?: boolean;
  /**
   * When the on-change trigger's trailing pass is due: the end of the debounce window
   * (newest stamp `computedAt` + narrowest window) a suppressed source change was
   * enqueued for. Set by `formDeriveRowTrigger` AFTER its enqueue succeeds, so later
   * writes in the same window enqueue nothing; cleared by every reconciler release; read
   * by `formDeriveTrailingPass`, which runs a pass only while the stored value equals the
   * one it was enqueued for. A gate, not a lock: nothing waits on it, nothing holds it, and
   * a stale value is corrected by the next release of any origin.
   */
  trailingPassAt?: Timestamp | null;
};

/**
 * How long a `computingSince` lock is honored before it is treated as abandoned.
 * Double `computeAggregations`' 30s: one derive pass pages several whole tables
 * inside a 300s trigger, and a shorter window would steal locks from healthy
 * holders. Theft is safe either way — the fence stops the loser — so on the
 * server this only tunes wasted work. On the client it bounds the "Calculating"
 * spinner: a wedged lock must stop spinning rather than show "stuck" copy.
 */
export const FORM_DERIVE_LOCK_STALE_MS = 60_000;

/**
 * Floor on BOTH members of `FormDeriveRefreshTriggers`: the on-change debounce
 * window directly, and the while-open interval after its minutes are converted
 * to milliseconds. One floor, because both parameters buy the same thing — the
 * shortest spacing between two derive passes — and a pass pages every table the
 * config names. Anything narrower is the configuration the read-cost analysis
 * refuses: an hour of typing against a 400-line table costs thousands of reads
 * at a minute and hundreds of thousands at a second, and an interval trigger
 * ticking faster than that costs the same reads with nobody typing at all.
 * Neither buys anything observable either, because a pass or a tick landing
 * while a pass still holds the lock only sets `recomputeRequested` and exits —
 * which is why the floor is one `FORM_DERIVE_LOCK_STALE_MS` window.
 */
export const FORM_DERIVE_REFRESH_MIN_MS = 60_000;

/** Marks one ArTable row as the projected output of a row-projection derive. */
export type FormDeriveRowOwner = {
  /** Which derive produced the row. */
  deriveId: FormDeriveId;
  /**
   * The group's stable identity within that derive:
   * `canonicalizeFormDeriveValue(groupValue)`, always — canonical, not raw, so
   * two cells referencing the same record under different cached labels
   * collapse to one projected row instead of two.
   */
  key: string;
};

/**
 * Prefix of every derive-owned row's document ID, so derive-owned rows are
 * recognizable without reading the FormType's derive config.
 */
export const FORM_DERIVE_ROW_ID_PREFIX = 'drv~';
